/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { defaultValue } from './PageMenu';

const PageMenuManager: ComponentManagerFn<'PageMenu'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'PageMenu'>(platform, 'PageMenu'),
    type: 'PageMenu',
    name: 'Page Menu',
    defaultValue,
    props: {},
    getDisplayText: (item) => 'Page Menu',
    category: 'Page Header',
    icon: { iconName: 'bars' },
    description: `**Overview**<br/>
The Page Menu component renders the standard "Hamburger" icon (three horizontal lines) used to toggle the application's main navigation sidebar or drawer.
**Note:** The standard application header automatically includes this control. This component is specifically intended for use within a **Custom Header** (PageHeader) where you have replaced the default top bar but still need to provide users with access to the main application menu.

**1. Functionality**<br/>
* **Navigation Toggle:** Clicking this icon expands or collapses the left-hand navigation pane (the sidebar containing your application's pages, folders, and external links).
* **Visual Standard:** It uses the universal "bars" icon, making it instantly recognizable to users as the primary trigger for the menu system.

**2. Configuration**<br/>
* **Zero Configuration:** This component requires no manual property setup (props: {}). 
* **Automatic Integration:** Once added to your layout, it automatically connects to the platform's internal navigation controller to manage the sidebar state without requiring custom logic or triggers.

**3. Best Use Cases**<br/>
* **Custom Headers:** Essential for maintaining consistent navigation access when you have hidden the default header to implement a bespoke corporate design.
* **Mobile-First Layouts:** Ensuring the menu trigger is prominently placed in a custom toolbar or action bar to facilitate navigation on smaller screens and touch devices.
* **Minimalist Dashboards:** Building a clean, "full-screen" dashboard layout that utilizes a custom-positioned menu button rather than a persistent top bar.`,
  };
};

export default PageMenuManager;
